import React from "react";
import { Users, MessagesSquare, Repeat, ScanSearch, Building2, ShieldCheck, GitBranch, CheckCircle2, ArrowDown, LayoutDashboard } from "lucide-react";

const STEPS = [
  { icon: Users, label: "Customer / employee", sub: "The person starting the interaction", tag: "SOURCE" },
  { icon: MessagesSquare, label: "Channel", sub: "Chat, call, email or workflow", tag: "SOURCE" },
  { icon: Repeat, label: "Bifröst", sub: "Connect · normalise · route", tag: "NORSE3" },
  { icon: ScanSearch, label: "Aegis input control", sub: "Inspect what enters the AI system", tag: "NORSE3" },
  { icon: Building2, label: "AI / model / business system", sub: "Your AI and core systems — unchanged", tag: "EXTERNAL" },
  { icon: ShieldCheck, label: "Aegis output control", sub: "Inspect what returns before release", tag: "NORSE3" },
  { icon: GitBranch, label: "Allow · Warn · Redact · Block · Hold · Escalate", sub: "Your configured action applies", tag: "NORSE3" },
  { icon: CheckCircle2, label: "Customer / workflow", sub: "The outcome the person receives", tag: "SOURCE" },
];

const tagStyle = (tag) =>
  tag === "EXTERNAL"
    ? "border-signal-amber/40 text-signal-amber bg-signal-amber/[0.06]"
    : tag === "SOURCE"
    ? "border-white/15 text-muted-foreground bg-white/[0.03]"
    : "border-primary/30 text-primary bg-primary/[0.06]";

export default function FSArchitectureFlow() {
  return (
    <div className="rounded-lg border border-white/10 bg-navy-900/70 p-5 sm:p-6 shadow-2xl shadow-black/40" data-testid="fs-architecture">
      <div className="flex items-center justify-between">
        <p className="text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">A regulated interaction</p>
        <span className="text-[0.7rem] text-muted-foreground/70">Illustrative</span>
      </div>

      <div className="mt-4 space-y-1.5">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const external = s.tag === "EXTERNAL";
          return (
            <div key={s.label}>
              <div className={`flex items-center gap-3 rounded-xl border px-3.5 py-2.5 ${external ? "border-signal-amber/30 bg-signal-amber/[0.04]" : "border-white/10 bg-navy-800/60"}`}>
                <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${external ? "bg-signal-amber/12 text-signal-amber" : "bg-primary/12 text-primary"}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-foreground leading-tight">{s.label}</span>
                  <span className="block text-xs text-muted-foreground leading-tight mt-0.5">{s.sub}</span>
                </span>
                <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[0.6rem] font-semibold tracking-wide ${tagStyle(s.tag)}`}>{s.tag}</span>
              </div>
              {i < STEPS.length - 1 && <div className="flex justify-center py-0.5" aria-hidden="true"><ArrowDown className="h-3.5 w-3.5 text-muted-foreground/40" /></div>}
            </div>
          );
        })}
      </div>

      <div className="mt-4 rounded-xl border border-primary/25 bg-primary/[0.05] p-4">
        <div className="flex items-center gap-2">
          <LayoutDashboard className="h-4 w-4 text-primary" />
          <p className="text-sm font-semibold text-foreground">Heimdall</p>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {["Monitoring", "Human review", "Policy", "Incidents", "Evidence", "Management information"].map((x) => (
            <span key={x} className="rounded-full border border-white/12 bg-navy-800/60 px-2.5 py-1 text-[0.68rem] font-medium text-foreground/85">{x}</span>
          ))}
        </div>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">norse3 <span className="text-foreground font-medium">surrounds and governs</span> the interaction — it does not replace the firm's AI or core systems.</p>
    </div>
  );
}
